import { FC, Fragment } from 'react';
import {
	NAPSpecialConditionPayload,
	NAPState,
	getNAPSpecialConditionDetail,
	submitNAPSpecialCondition,
	verifyNAPSpecialCondition,
} from '@/libs/fleet-api/services/nap';
import {
	LoadingOutlined,
	EditOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Col, Divider, Form, Row, Table } from 'antd';
import { useForm } from '@/libs/antd/form';
import SpecialApplicationModal, {
	useSpecialConditionModal,
} from '../modals/SpecialConditionModal';
import { useModals } from '@/components/modals/ModalProvider';
import TextArea from 'antd/es/input/TextArea';
import { useUserData } from '@/libs/fleet-api/hooks';
import rule from '@/libs/antd/rules';

const NAPSpecialCondition: FC<{
	napId?: number;
	napState?: NAPState;
	prospectId: string;
	onSubmitted: () => void;
}> = ({ prospectId, napId, napState, onSubmitted }) => {
	const { groupName } = useUserData() ?? {};

	const getNAPSpecialConditionDetailQuery = useQuery({
		queryKey: ['getNAPSpecialConditionDetail', napId, prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, napId, prospectId] = queryKey;
			if (!napId || !prospectId) return null;
			return getNAPSpecialConditionDetail({
				napId,
				prospectId,
			});
		},
	});

	const submitSpecialConditionMutation = useMutation({
		mutationFn: submitNAPSpecialCondition,
	});

	const verifySpecialConditionMutation = useMutation({
		mutationFn: verifyNAPSpecialCondition,
	});

	const { openPreviewModal } = useModals();
	const specialConditionModal = useSpecialConditionModal();
	const { form, fieldName, value } = useForm<NAPSpecialConditionPayload>();

	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	if (getNAPSpecialConditionDetailQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const { data } = getNAPSpecialConditionDetailQuery.data ?? {};
	const statusOptions = data?.specialConditionStatusOptions ?? [];
	const subjectOptions = data?.specialConditionSubjectOptions ?? [];
	const approverOptions = data?.finalApproverOptions ?? [];

	return (
		<Fragment>
			<Row style={{ marginBlock: 20 }}>
				<Col flex="auto" />
				<Col flex="none">
					<Button
						style={{
							borderRadius: 50,
							fontSize: 12,
							height: 24,
							fontWeight: 700,
							...(!isReadOnly && {
								borderColor: '#1557BC',
								color: '#1557BC',
							}),
						}}
						icon={'+'}
						disabled={isReadOnly}
						onClick={async () => {
							const payload = await specialConditionModal.open({
								type: 'new',
								prospectId,
								statusOptions,
								subjectOptions,
								approverOptions,
							});

							if (!payload) return;
							form.setFieldValue(fieldName('specialConditionSubmissions'), [
								...(value?.specialConditionSubmissions ?? []),
								payload,
							]);
						}}
					>
						Add Another
					</Button>
				</Col>
			</Row>
			<Form
				form={form}
				onFinish={async (payload) => {
					if (groupName === 'FRM') {
						await submitSpecialConditionMutation.mutateAsync(payload);
					}

					if (groupName === 'CCA') {
						await verifySpecialConditionMutation.mutateAsync(payload);
					}

					onSubmitted();
				}}
			>
				<Form.Item
					name={fieldName('id')}
					initialValue={data?.id}
					noStyle
				/>
				<Form.Item
					name={fieldName('napId')}
					initialValue={napId}
					noStyle
				/>
				<Form.Item
					name={fieldName('specialConditionSubmissions')}
					initialValue={data?.specialConditionSubmission ?? []}
					noStyle
				>
					<Table
						bordered
						pagination={false}
						dataSource={value?.specialConditionSubmissions?.map((data, key) => {
							return { ...data, key };
						})}
						columns={[
							{
								title: 'Action',
								colSpan: 2,
								width: 100,
								align: 'center',
								render: (_, record, i) => (
									<a
										style={isReadOnly ? { color: '#bababa' } : {}}
										onClick={async (e) => {
											e.preventDefault();
											if (!isReadOnly) {
												const payload = await specialConditionModal.open({
													type: 'edit',
													record,
													prospectId,
													statusOptions,
													subjectOptions,
													approverOptions,
												});

												if (!payload) return;
												form.setFieldValue(
													fieldName('specialConditionSubmissions'),
													value?.specialConditionSubmissions?.map(
														(data, index) => {
															return index === i ? payload : data;
														},
													),
												);
											}
										}}
									>
										<EditOutlined />
									</a>
								),
							},
							{
								colSpan: 0,
								width: 100,
								align: 'center',
								render: (_, __, index) => (
									<a
										style={isReadOnly ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isReadOnly) {
												form.setFieldValue(
													fieldName('specialConditionSubmissions'),
													value?.specialConditionSubmissions?.filter(
														(_, i) => index !== i,
													),
												);
											}
										}}
									>
										<DeleteOutlined />
									</a>
								),
							},
							{
								title: 'Perihal',
								render: (_, record) =>
									subjectOptions.find((data) => data.id === record.subjectId)
										?.value,
							},
							{
								title: 'Ketentuan',
								render: (_, record) => record.provision,
							},
							{
								title: 'Usulan',
								render: (_, record) => record.proposal,
							},
							{
								title: 'Pemutus Terakhir',
								render: (_, record) => (
									<ul style={{ margin: 0, paddingLeft: 16 }}>
										{record.finalApprover.map((id) => {
											return (
												<li>
													{approverOptions.find((option) => option.id === id)
														?.value ?? id}
												</li>
											);
										})}
									</ul>
								),
							},
							{
								title: 'Pengajuan Kondisi Khusus',
								render: (_, record) => record.specialCondition,
							},
							{
								title: 'Status Pengajuan Kondisi Khusus',
								render: (_, record) =>
									statusOptions.find(
										(data) => data.id === record.specialConditionStatusId,
									)?.value,
							},
							{
								title: 'Evidence',
								render: (_, record) => {
									if (record.evidenceFile) {
										return (
											<Button
												type="link"
												onClick={() => {
													openPreviewModal(record.evidenceFile);
												}}
											>
												Evidence
											</Button>
										);
									}
									return null;
								},
							},
						]}
					/>
				</Form.Item>

				<Divider />

				<Form.Item
					label="Informasi Tambahan"
					name={fieldName('notes')}
				>
					<TextArea
						rows={4}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>

				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						label={groupName === 'CCA' ? 'Verification Notes' : 'Notes CCA'}
						style={{ marginTop: 20 }}
						name={'verificationNotes'}
						initialValue={data?.verificationNotes}
						rules={groupName === 'CCA' ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={
						submitSpecialConditionMutation.isPending ||
						verifySpecialConditionMutation.isPending
						// isReadOnly
					}
					loading={
						submitSpecialConditionMutation.isPending ||
						verifySpecialConditionMutation.isPending
					}
				>
					NEXT
				</Button>
			</Form>
			<SpecialApplicationModal {...specialConditionModal} />
		</Fragment>
	);
};

export default NAPSpecialCondition;
